// src/features/wf2/api.js
import api from "../../api/axios";
import { socket, RT } from "../../api/realtime";

const BASE = "/wf_2";

/**
 * ✅ Evento local (browser) para atualizar a lista instantaneamente
 * mesmo quando o backend/socket não devolve o evento para o próprio emissor.
 */
export const W2_LOCAL_EVENT = "wf2:local_rt";

// helper local para normalizar prioridade caso chegue como "Alta/Média/Baixa"
const normalizePrioridade = (v) => {
  if (!v && v !== 0) return "";
  const u = String(v).trim().toUpperCase();
  if (/^P[0-5]$/.test(u)) return u;
  if (u === "ALTA") return "P1";
  if (u === "MEDIA" || u === "MÉDIA") return "P3";
  if (u === "BAIXA") return "P5";
  return "";
};

/* ========================================================================
   Helpers de RealTime para WF-2
   ======================================================================== */

function getCodigoUnicoRT(out, payload = {}) {
  const p = payload || {};
  const d = out?.data || out || {};

  const w2 =
    d?.programado?.data ??
    d?.programado?.w2 ??
    d?.programado?.row ??
    d?.w2 ??
    d?.data?.w2 ??
    d?.row ??
    d?.data?.row ??
    d?.data ??
    null;

  return (
    p.codigo_unico ??
    p.codigo ??
    p.codigoUnico ??
    d.codigo_unico ??
    d.codigo ??
    w2?.codigo_unico ??
    w2?.codigo ??
    out?.codigo_unico ??
    out?.id ??
    w2?.id ??
    null
  );
}

function getW2RowRT(out) {
  const d = out?.data || out || {};

  return (
    d?.programado?.data ??
    d?.programado?.w2 ??
    d?.programado?.row ??
    d?.w2 ??
    d?.data?.w2 ??
    d?.row ??
    d?.data?.row ??
    d?.data ??
    null
  );
}

function emitRTW2(kind, out, payload = {}) {
  try {
    const codigo_unico = getCodigoUnicoRT(out, payload);
    const w2 = getW2RowRT(out);

    const pack = {
      from: "wf_2",
      topic: kind, // ✅ ajuda quem consome
      kind,
      codigo_unico: codigo_unico ?? null,
      ...(w2 && typeof w2 === "object" ? { w2 } : {}),
      payload: out, // pode ser {ok,data} ou o payload do backend
    };

    // ✅ via socket (para outros usuários) — se tiver conectado
    // Obs: seu backend não precisa “ouvir” isso, então isso é best-effort.
    try {
      if (socket && socket.connected) socket.emit(kind, pack);
    } catch (_) {}

    // ✅ via evento local (para ESTE usuário)
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      try {
        window.dispatchEvent(new CustomEvent(W2_LOCAL_EVENT, { detail: pack }));
      } catch (_) {
        try {
          const ev = document.createEvent("CustomEvent");
          ev.initCustomEvent(W2_LOCAL_EVENT, false, false, pack);
          window.dispatchEvent(ev);
        } catch (_) {}
      }
    }
  } catch (e) {
    console.debug("[wf2/api RT error]", e?.message || e);
  }
}

/* ========================================================================
   API
   ======================================================================== */

export async function listW2({
  limit = 5000,
  offset = 0,
  data_inicio,
  data_fim,
  status,
  status_wf2,
  gerencia,
  meta,
} = {}) {
  const params = { limit, offset, _: Date.now() };
  if (data_inicio) params.data_inicio = data_inicio;
  if (data_fim) params.data_fim = data_fim;
  if (status || status_wf2) params.status_wf2 = status || status_wf2;
  if (gerencia) params.gerencia = gerencia;
  if (meta) params.meta = meta;

  const { data } = await api.get(`${BASE}/listar`, { params });

  if (Array.isArray(data)) return data;
  if (data?.ok) return data.data || data.rows || [];
  return data?.data || data?.rows || [];
}

export async function getW2ById(id) {
  if (!id) throw new Error("id obrigatório em getW2ById");
  const { data } = await api.get(`${BASE}/${id}`, { params: { _: Date.now() } });
  return data?.data || data;
}

export async function listDesviosEditarOpcoes() {
  const tries = [
    { url: "/desvios_editar/opcoes", params: { _: Date.now() } },
    { url: "/desvios-editar/opcoes", params: { _: Date.now() } },
    { url: "/bases/desvios_editar/opcoes", params: { _: Date.now() } },
    { url: "/bases/desvios-editar/opcoes", params: { _: Date.now() } },
    { url: "/desvios_editar/listar", params: { ativo: 1, limit: 5000, offset: 0 } },
    { url: "/desvios-editar/listar", params: { ativo: 1, limit: 5000, offset: 0 } },
    { url: "/bases/desvios_editar/listar", params: { ativo: 1, limit: 5000, offset: 0 } },
    { url: "/bases/desvios-editar/listar", params: { ativo: 1, limit: 5000, offset: 0 } },
  ];

  let lastErr = null;
  for (const t of tries) {
    try {
      const { data } = await api.get(t.url, { params: t.params });
      const hasArrayPayload =
        Array.isArray(data) ||
        Array.isArray(data?.data) ||
        Array.isArray(data?.rows);
      if (!hasArrayPayload) continue;

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.rows)
        ? data.rows
        : [];
      return arr;
    } catch (e) {
      lastErr = e;
    }
  }

  throw lastErr || new Error("Falha ao listar opções de desvios_editar.");
}

export async function listDesviosNegarCausadores() {
  const tries = [
    { url: "/desvios_negar/causadores", params: { _: Date.now() } },
    { url: "/desvios-negar/causadores", params: { _: Date.now() } },
    { url: "/bases/desvios_negar/causadores", params: { _: Date.now() } },
    { url: "/bases/desvios-negar/causadores", params: { _: Date.now() } },
    { url: "/desvios_negar/opcoes", params: { _: Date.now() } },
    { url: "/desvios-negar/opcoes", params: { _: Date.now() } },
    { url: "/bases/desvios_negar/opcoes", params: { _: Date.now() } },
    { url: "/bases/desvios-negar/opcoes", params: { _: Date.now() } },
  ];

  let lastErr = null;
  for (const t of tries) {
    try {
      const { data } = await api.get(t.url, { params: t.params });
      const hasArrayPayload =
        Array.isArray(data) ||
        Array.isArray(data?.data) ||
        Array.isArray(data?.rows);
      if (!hasArrayPayload) continue;

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.rows)
        ? data.rows
        : [];
      return arr;
    } catch (e) {
      lastErr = e;
    }
  }

  throw lastErr || new Error("Falha ao listar causadores de desvios_negar.");
}

export async function listDesviosNegarPrimarias(causador) {
  if (!causador) return [];

  const tries = [
    { url: "/desvios_negar/causas-primarias", params: { causador, _: Date.now() } },
    { url: "/desvios-negar/causas-primarias", params: { causador, _: Date.now() } },
    { url: "/bases/desvios_negar/causas-primarias", params: { causador, _: Date.now() } },
    { url: "/bases/desvios-negar/causas-primarias", params: { causador, _: Date.now() } },
    { url: "/desvios_negar/opcoes", params: { causador, _: Date.now() } },
    { url: "/desvios-negar/opcoes", params: { causador, _: Date.now() } },
    { url: "/bases/desvios_negar/opcoes", params: { causador, _: Date.now() } },
    { url: "/bases/desvios-negar/opcoes", params: { causador, _: Date.now() } },
  ];

  let lastErr = null;
  for (const t of tries) {
    try {
      const { data } = await api.get(t.url, { params: t.params });
      const hasArrayPayload =
        Array.isArray(data) ||
        Array.isArray(data?.data) ||
        Array.isArray(data?.rows);
      if (!hasArrayPayload) continue;

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.rows)
        ? data.rows
        : [];
      return arr;
    } catch (e) {
      lastErr = e;
    }
  }

  throw lastErr || new Error("Falha ao listar causas primárias de desvios_negar.");
}

export async function salvarW2(payload = {}) {
  try {
    const { data } = await api.post(`${BASE}/salvar`, payload);
    const out = data?.ok !== undefined ? data : { ok: true, data };
    emitRTW2(RT.UPDATED, out, payload);
    return out;
  } catch (e) {
    const st = e?.response?.status;
    if (st && ![404, 405].includes(st)) throw e;
  }

  const { data } = await api.post(`${BASE}`, payload);
  const out = data?.ok !== undefined ? data : { ok: true, data };
  emitRTW2(RT.CREATED, out, payload);
  return out;
}

export async function editarW2(payload = {}) {
  const id = payload?.id;

  const tries = [
    { method: "post", url: `${BASE}/editar`, body: payload },
    ...(id
      ? [
          { method: "patch", url: `${BASE}/${id}`, body: payload },
          { method: "put", url: `${BASE}/${id}`, body: payload },
        ]
      : []),
  ];

  let lastErr = null;
  for (const t of tries) {
    try {
      const { data } = await api.request({
        method: t.method,
        url: t.url,
        data: t.body,
      });
      const out = data?.ok !== undefined ? data : { ok: true, data };
      emitRTW2(RT.UPDATED, out, payload);
      return out;
    } catch (e) {
      const st = e?.response?.status;
      if (st && ![404, 405].includes(st)) throw e;
      lastErr = e;
    }
  }

  throw lastErr || new Error("Falha ao salvar no backend em todas as rotas.");
}

export async function programarW2(payload = {}) {
  const body = {
    ...payload,
    status_wf2: "EM_VALIDACAO_AUTOMATICA",
    ...(payload?.prioridade
      ? { prioridade: normalizePrioridade(payload.prioridade) || payload.prioridade }
      : {}),
  };

  const { data } = await api.post(`${BASE}/programar`, body);
  const out = data?.ok !== undefined ? data : { ok: true, data };
  emitRTW2(RT.UPDATED, out, body);
  return out;
}

export async function autoMetaW2({ id = null, codigo_unico = null } = {}) {
  const body = { id, codigo_unico };
  const { data } = await api.post(`${BASE}/auto_meta`, body);
  const out = data?.ok !== undefined ? data : { ok: true, data };
  emitRTW2(RT.UPDATED, out, body);
  return out;
}

/**
 * Confirma pre-meta manualmente (ignora timer)
 * Botão que o usuário clica para aprovar a meta antes do timer expirar.
 */
export async function confirmarMetaW2({ id = null, codigo_unico = null } = {}) {
  const body = { id, codigo_unico };
  const { data } = await api.post(`${BASE}/confirmar_meta`, body);
  const out = data?.ok !== undefined ? data : { ok: true, data };
  emitRTW2(RT.UPDATED, out, body);
  return out;
}

export async function novaProgramacaoW2(payload = {}) {
  const body = {
    ...payload,
    ...(payload?.prioridade
      ? { prioridade: normalizePrioridade(payload.prioridade) || payload.prioridade }
      : {}),
  };

  const { data } = await api.post(`${BASE}/nova_programacao`, body);
  const out = data?.ok !== undefined ? data : { ok: true, data };
  emitRTW2(RT.CREATED, out, body);
  return out;
}

export async function negarW2(payload = {}) {
  const body = { ...payload, status_wf2: "NEGADO" };
  const id = body?.id;

  const tries = [
    { method: "post", url: `${BASE}/negar`, body },
    { method: "post", url: `${BASE}/editar`, body },
    ...(id
      ? [
          { method: "patch", url: `${BASE}/${id}`, body },
          { method: "put", url: `${BASE}/${id}`, body },
        ]
      : []),
  ];

  let lastErr = null;
  for (const t of tries) {
    try {
      const { data } = await api.request({
        method: t.method,
        url: t.url,
        data: t.body,
      });
      const out = data?.ok !== undefined ? data : { ok: true, data };
      emitRTW2(RT.UPDATED, out, body);
      return out;
    } catch (e) {
      const st = e?.response?.status;
      if (st && ![404, 405, 501].includes(st)) throw e;
      lastErr = e;
    }
  }

  throw lastErr || new Error("Falha ao negar no backend em todas as rotas.");
}

export async function moveDateW2({
  codigo_unico,
  data,
  motivo,
  causador,
  causa_primaria,
}) {
  const { data: outRaw } = await api.post(`${BASE}/mover-data`, {
    codigo_unico,
    data,
    motivo,
    causador,
    causa_primaria,
  });
  const out = outRaw?.ok ? outRaw : { ok: true, ...outRaw };
  emitRTW2(RT.MOVED, out, { codigo_unico });
  return out;
}

export async function advanceStageW2({ codigo_unico, etapa_nova }) {
  const { data: outRaw } = await api.post(`${BASE}/avancar`, {
    codigo_unico,
    etapa_nova,
  });
  const out = outRaw?.ok ? outRaw : { ok: true, ...outRaw };
  emitRTW2(RT.UPDATED, out, { codigo_unico });
  return out;
}
